import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Typography, Button, CircularProgress, Collapse, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import GroupIcon from '@mui/icons-material/Group';
import TuneIcon from '@mui/icons-material/Tune';
import { RootState } from '../../../store';
import { GroupsTeamsService } from '../../../services/groups-teams.service';
import { TagsService } from '../../../services/tags-service';
import { GT_COLORS, WatchButton, isWatchingTeam } from '../../../components/groups-teams/shared';
import { TeamDetailsCard, type TagOption } from './cards/TeamDetailsCard';
import { TeamLeadersCard } from './cards/TeamLeadersCard';
import { TeamMembersCard } from './cards/TeamMembersCard';
import { FeatureConfigCard } from './cards/FeatureConfigCard';
import { DefaultContactsCard } from './cards/DefaultContactsCard';
import { EmergencyScheduleSection } from './cards/EmergencyScheduleSection';
import { DeleteTeamModal } from './modals/DeleteTeamModal';
import { AddMemberModal } from './modals/AddMemberModal';
import { AddLeaderModal } from './modals/AddLeaderModal';
import { permissionCheck } from '../../../helpers/permissionCheck';
import { customerPermissionFlags } from '../../../constants/systemPermissions.constants';
import { toast } from 'react-toastify';
import { stripCheckOnMeLegacyFields } from '../../../utils/checkOnMeLegacyFields';

type EscalationBehavior = 'ems' | 'org';

/**
 * Blocks all navigation (sidebar links, programmatic navigate, back/forward, etc.)
 * when `shouldBlock` is true. Works with BrowserRouter by intercepting
 * history.pushState, history.replaceState, and the popstate event.
 */
const useNavigationBlock = (shouldBlock: boolean) => {
    const [blocked, setBlocked] = useState(false);
    const pendingNavRef = useRef<(() => void) | null>(null);
    const shouldBlockRef = useRef(shouldBlock);
    shouldBlockRef.current = shouldBlock;

    useEffect(() => {
        if (!shouldBlock) return;

        const origPush = window.history.pushState.bind(window.history);
        const origReplace = window.history.replaceState.bind(window.history);

        window.history.pushState = function (data: any, unused: string, url?: string | URL | null) {
            if (shouldBlockRef.current) {
                setBlocked(true);
                pendingNavRef.current = () => {
                    origPush(data, unused, url);
                    // Dispatch popstate so React Router picks up the URL change
                    window.dispatchEvent(new PopStateEvent('popstate'));
                };
                return;
            }
            origPush(data, unused, url);
        };

        window.history.replaceState = function (data: any, unused: string, url?: string | URL | null) {
            if (shouldBlockRef.current) {
                setBlocked(true);
                pendingNavRef.current = () => {
                    origReplace(data, unused, url);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                };
                return;
            }
            origReplace(data, unused, url);
        };

        // Block browser back/forward — capture source URL at setup time
        const sourceUrl = window.location.href;
        const onPopState = () => {
            if (shouldBlockRef.current) {
                // Restore the source URL (location.href has already changed by the time popstate fires)
                origPush(null, '', sourceUrl);
                setBlocked(true);
                pendingNavRef.current = () => window.history.go(-1);
            }
        };
        window.addEventListener('popstate', onPopState);

        return () => {
            window.history.pushState = origPush;
            window.history.replaceState = origReplace;
            window.removeEventListener('popstate', onPopState);
        };
    }, [shouldBlock]);

    const proceed = useCallback(() => {
        setBlocked(false);
        const nav = pendingNavRef.current;
        pendingNavRef.current = null;
        // Temporarily disable blocking so the replayed nav goes through
        shouldBlockRef.current = false;
        nav?.();
    }, []);

    const reset = useCallback(() => {
        setBlocked(false);
        pendingNavRef.current = null;
    }, []);

    return { blocked, proceed, reset };
};

export const TeamDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const { groupId, teamId } = useParams<{ groupId: string; teamId: string }>();
    const { l1Groups, l3, loading, mySubscriptions } = useSelector((state: RootState) => state.groupsTeams);

    // Local form state
    const [name, setName] = useState('');
    const [location, setLocation] = useState('');
    const [parentGroupId, setParentGroupId] = useState<string | null>(groupId || null);
    const [selectedTags, setSelectedTags] = useState<TagOption[]>([]);
    const [allTags, setAllTags] = useState<TagOption[]>([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [checkOnMeEnabled, setCheckOnMeEnabled] = useState<boolean | undefined>(undefined);
    const [escalation, setEscalation] = useState<EscalationBehavior>('ems');
    const [isOverride, setIsOverride] = useState(false);
    const [contacts, setContacts] = useState<{ full_name: string; phone_number: string }[]>([]);
    const [contactPhoneErrors, setContactPhoneErrors] = useState<string[]>([]);
    const [contactNameErrors, setContactNameErrors] = useState<string[]>([]);
    const [savedContacts, setSavedContacts] = useState<{ full_name: string; phone_number: string }[]>([]);
    // Tracks whether the team already had "org" escalation with valid contacts when loaded
    // If true, we don't block navigation (it's a pre-existing valid state)
    const [loadedWithValidOrg, setLoadedWithValidOrg] = useState(false);
    // True when the effective-settings fetch failed — disables the contacts
    // card rather than trusting whatever is currently on screen to be right,
    // and definitely rather than wiping it to empty.
    const [contactsUnavailable, setContactsUnavailable] = useState(false);

    const canManageTeams = permissionCheck(customerPermissionFlags.TEAM_LEADER);

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [addMemberOpen, setAddMemberOpen] = useState(false);
    const [addLeaderOpen, setAddLeaderOpen] = useState(false);
    const [detailsExpanded, setDetailsExpanded] = useState(false);

    const isValidObjectId = (id?: string) => !!id && /^[0-9a-fA-F]{24}$/.test(id);

    // Refs kept in sync every render so the async getTeamSettings callback
    // below can read the latest values without retriggering the effect.
    const contactsRef = useRef(contacts);
    contactsRef.current = contacts;
    const savedContactsRef = useRef(savedContacts);
    savedContactsRef.current = savedContacts;
    // Bumped on every fetch so a response can tell whether a newer fetch
    // has since been issued (team switch, or an unrelated l3 refetch) and
    // ignore itself if so.
    const teamSettingsRequestIdRef = useRef(0);

    useEffect(() => {
        // Clear stale L3 data before fetching new team
        GroupsTeamsService.dispatch.setTeamDetail(null);
        if (isValidObjectId(groupId) && isValidObjectId(teamId)) {
            GroupsTeamsService.fetchTeamDetail(groupId!, teamId!, 0, 5, (ok, msg) => {
                if (!ok) toast.error(msg || 'Failed to load team.');
            });
        } else if (groupId && !isValidObjectId(teamId)) {
            // Bad teamId in URL — redirect back to group page
            navigate(`/groups-teams/${groupId}`, { replace: true });
            return;
        }
        // Ensure L1 groups are loaded for the parent group dropdown
        if (l1Groups.length === 0) GroupsTeamsService.fetchOverview();
        GroupsTeamsService.loadMySubscriptions();
    }, [groupId, teamId]);

    // Sync store → local state when data loads
    useEffect(() => {
        if (l3?.team) {
            setName(l3.team.name || '');
            setLocation((l3.team as any).location || '');
            setParentGroupId(l3.parentGroup?.id || groupId || null);

            setContactPhoneErrors([]);
            setContactNameErrors([]);

            // Fetch effective settings (team override → org default fallback).
            // Contacts come from the resolved settingsV2 value here, not
            // team.settings.contacts — node-api's escalation dispatch
            // (getActiveContactsEx) reads that same resolved value, so the
            // page must show exactly what dispatch will use.
            if (teamId) {
                const requestId = ++teamSettingsRequestIdRef.current;
                GroupsTeamsService.getTeamSettings(teamId).then((effective) => {
                    // Ignore this response if a newer fetch has since been
                    // issued (team switch, or an unrelated l3 refetch while
                    // this one was in flight) — applying it would clobber
                    // fresher state with stale data.
                    if (requestId !== teamSettingsRequestIdRef.current) return;

                    // Don't clobber an in-progress, unsaved contact edit —
                    // the fetch was issued before the user's edit landed.
                    const serialize = (arr: { full_name: string; phone_number: string }[]) =>
                        arr.map((c) => `${c.full_name.trim()}|${c.phone_number}`).join(',');
                    const isDirty = serialize(contactsRef.current) !== serialize(savedContactsRef.current);
                    if (isDirty) return;

                    if (effective) {
                        setCheckOnMeEnabled(effective.checkOnMeEnabled);
                        // Coerce unsupported values (e.g. 'notify' from org defaults) to 'ems'
                        const esc = effective.escalationBehavior === 'org' ? 'org' : 'ems';
                        setEscalation(esc);
                        setIsOverride(effective.isOverride ?? false);

                        // Fall back to the legacy mirror only if this response
                        // didn't carry a `contacts` array at all (e.g. an older
                        // API build) — an empty array IS a valid resolved
                        // state and must not be replaced by a stale legacy one.
                        const legacyContacts = (l3.team as any)?.settings?.contacts;
                        const loadedContacts = Array.isArray(effective.contacts)
                            ? effective.contacts
                            : (Array.isArray(legacyContacts) ? legacyContacts : []);
                        setContacts(loadedContacts);
                        setSavedContacts(loadedContacts);
                        setContactsUnavailable(false);

                        // Track if the team loaded with a valid "org" config (has contacts already)
                        const isOrg = effective.escalationBehavior === 'org' && effective.checkOnMeEnabled;
                        const hasValidContacts = loadedContacts.some((c: any) =>
                            c.full_name.trim().length > 0 && c.phone_number.replace(/[^0-9]/g, '').length === 11
                        );
                        setLoadedWithValidOrg(!!(isOrg && hasValidContacts));
                    } else {
                        // Fetch failed — keep whatever contacts are already
                        // shown rather than wiping them client-side, and
                        // disable editing until a trustworthy value loads.
                        setContactsUnavailable(true);
                        setLoadedWithValidOrg(false);
                    }
                });
            }

            // Load tags and resolve team's tag IDs
            setTagsLoading(true);
            TagsService.fetchAll().then((tags) => {
                const opts: TagOption[] = (tags || []).map((t: any) => ({ id: t._id || t.id, name: t.name, color: t.color }));
                setAllTags(opts);
                const matched = (l3.team.tags || [])
                    .map((tagId: string) => opts.find((o) => o.id === tagId))
                    .filter(Boolean) as TagOption[];
                setSelectedTags(matched);
            }).catch((err) => console.error('[TeamDetail] tag fetch failed', err)).finally(() => setTagsLoading(false));
        }
    }, [l3]);

    // ── Refs for latest local state (used by saveTeam) ──
    const nameRef = useRef(name);
    nameRef.current = name;
    const locationRef = useRef(location);
    locationRef.current = location;
    const parentGroupIdRef = useRef(parentGroupId);
    parentGroupIdRef.current = parentGroupId;
    const selectedTagsRef = useRef(selectedTags);
    selectedTagsRef.current = selectedTags;
    const checkOnMeRef = useRef(checkOnMeEnabled);
    checkOnMeRef.current = checkOnMeEnabled;
    const escalationRef = useRef(escalation);
    escalationRef.current = escalation;

    /** Persist the full team, merging in any overrides. */
    const saveTeam = useCallback(async (overrides?: Record<string, any>) => {
        if (!teamId) return;
        const currentName = overrides?.name ?? nameRef.current;
        if (!currentName.trim()) return;

        // Resolve tags — create any new ones
        const tags: TagOption[] = overrides?.selectedTags ?? selectedTagsRef.current;
        const finalTagIds: string[] = [];
        try {
            for (const tag of tags) {
                if (tag.isNew) {
                    const created = await TagsService.createTag({ name: tag.name });
                    if (!created) {
                        toast.error(`Failed to create tag "${tag.name}".`);
                        return;
                    }
                    finalTagIds.push((created as any)._id || (created as any).id);
                } else {
                    finalTagIds.push(tag.id);
                }
            }
        } catch {
            toast.error('Failed to create tag. Please try again.');
            return;
        }

        const team = l3?.team as any;

        // Only include Check On Me settings if user explicitly changed them or team has an override.
        // Prevents inherited org defaults from being saved as team-level overrides.
        const comExplicitlyChanged = overrides?.checkOnMeEnabled !== undefined || overrides?.escalation !== undefined;
        const esc = overrides?.escalation ?? escalationRef.current;
        const isComplete = (c: any) =>
            c.full_name.trim().length > 0 && c.phone_number.replace(/[^0-9]/g, '').length === 11;

        const teamSettings = stripCheckOnMeLegacyFields(team?.settings);
        if (comExplicitlyChanged || isOverride) {
            teamSettings.checkOnMeEnabled = overrides?.checkOnMeEnabled ?? checkOnMeRef.current;
            teamSettings.escalationBehavior = esc;

            // Legacy fields — update-team dual-writes these into settingsV2.overrides
            // too, which is what node-api's escalation dispatch actually reads.
            // For 'org', only set defaultContact=true if valid contacts already exist —
            // that flag becomes checkOnMe.escalation.useOrgContacts, so it must not be
            // true with empty contacts. Source from the live editing ref only — the
            // legacy `contacts` field was just stripped above and must not resurrect it.
            if (esc === 'org') {
                const existing = contactsRef.current || [];
                teamSettings.defaultContact = existing.some(isComplete);
            } else {
                teamSettings.defaultContact = false;
                teamSettings.calendarEnabled = false;
            }
        }

        // Only include contacts when explicitly passed — never merge in-progress edits from the ref
        if (overrides?.contacts !== undefined) {
            teamSettings.contacts = overrides.contacts;
            // Only let contact validity drive defaultContact when in org mode —
            // otherwise the legacy sync block above already set it to false.
            if (esc === 'org') {
                teamSettings.defaultContact = overrides.contacts.some(isComplete);
            }
        }

        await GroupsTeamsService.updateTeam(teamId, {
            name: (overrides?.name ?? nameRef.current).trim(),
            location: (overrides?.location ?? locationRef.current).trim() || undefined,
            groupId: overrides?.parentGroupId ?? parentGroupIdRef.current,
            tags: finalTagIds,
            membersIds: (team?.membersIds?.length ? team.membersIds : null) || l3?.members?.map((m: any) => m.id || m._id) || [],
            leadersIds: (team?.leadersIds?.length ? team.leadersIds : null) || l3?.leaders?.map((l: any) => l.id) || [],
            settings: teamSettings,
        }, (isValid, message) => {
            if (isValid) {
                toast.success('Team updated.');
                GroupsTeamsService.fetchTeamDetail(groupId!, teamId!);
            } else {
                toast.error(message || 'Failed to update team.');
            }
        });
    }, [teamId, groupId, l3]);

    const handleDelete = () => {
        if (!teamId) return;
        setDeleteOpen(false);
        GroupsTeamsService.deleteTeam(teamId, (isValid, message) => {
            if (isValid) {
                toast.success('Team deleted.');
                // Bypass the missing-contacts nav blocker — the team no longer exists.
                navBlock.reset();
                navigate(`/groups-teams/${groupId}`);
                navBlock.proceed();
            } else {
                toast.error(message || 'Failed to delete team.');
            }
        });
    };

    const handleRemoveMember = (userId: string) => {
        if (!teamId || !groupId) return;
        GroupsTeamsService.removeMember(teamId, userId, groupId, (isValid, message) => {
            if (isValid) {
                toast.success('Member removed.');
                GroupsTeamsService.fetchTeamDetail(groupId, teamId);
            } else {
                toast.error(message || 'Failed to remove member.');
            }
        });
    };

    const handleAddMemberDone = () => {
        // Brief delay to allow node-api to finish updating membersIds
        if (groupId && teamId) setTimeout(() => GroupsTeamsService.fetchTeamDetail(groupId, teamId), 500);
    };

    const handleAddLeaderDone = () => {
        // Brief delay to allow node-api to finish updating leadersIds
        if (groupId && teamId) setTimeout(() => GroupsTeamsService.fetchTeamDetail(groupId, teamId), 500);
    };

    const handleRemoveLeader = (leaderId: string) => {
        if (!teamId || loading.saving) return;
        const currentLeaderIds = ((l3?.team as any)?.leadersIds || l3?.leaders?.map((l: any) => l.id) || []) as string[];
        const newLeaderIds = currentLeaderIds.filter((id: string) => id !== leaderId);
        const team = l3?.team as any;
        // Preserve existing settings without materializing inherited values
        const teamSettings = stripCheckOnMeLegacyFields(team?.settings);
        if (isOverride) {
            teamSettings.checkOnMeEnabled = checkOnMeRef.current;
            teamSettings.escalationBehavior = escalationRef.current;
        }
        GroupsTeamsService.updateTeam(teamId, {
            name: nameRef.current.trim() || team?.name || '',
            location: locationRef.current.trim() || team?.location || undefined,
            groupId: parentGroupIdRef.current || team?.groupId || l3?.parentGroup?.id || groupId || '',
            membersIds: (team?.membersIds?.length ? team.membersIds : null) || l3?.members?.map((m: any) => m.id || m._id) || [],
            tags: selectedTagsRef.current.map((t) => t.id),
            settings: teamSettings,
            leadersIds: newLeaderIds,
        }, (isValid, message) => {
            if (isValid) {
                toast.success('Leader removed.');
                if (groupId && teamId) GroupsTeamsService.fetchTeamDetail(groupId, teamId);
            } else {
                toast.error(message || 'Failed to remove leader.');
            }
        });
    };

    const validateAndSaveContacts = useCallback(() => {
        const phoneErrs: string[] = [];
        const nameErrs: string[] = [];
        let valid = true;

        contactsRef.current.forEach((c, i) => {
            const hasName = c.full_name.trim().length > 0;
            const digits = c.phone_number.replace(/[^0-9]/g, '');
            const hasPhone = digits.length > 1; // more than just the "1" from +1

            if (hasPhone && !hasName) {
                nameErrs[i] = 'Name is required';
                valid = false;
            }
            if (hasName && !hasPhone) {
                phoneErrs[i] = 'Phone number is required';
                valid = false;
            }
            if (hasName && hasPhone && digits.length !== 11) {
                phoneErrs[i] = 'Must be +1 followed by 10 digits';
                valid = false;
            }
        });

        setContactPhoneErrors(phoneErrs);
        setContactNameErrors(nameErrs);

        // Check if all contacts are blank (user cleared them)
        const allBlank = contactsRef.current.every((c) => {
            const hasName = c.full_name.trim().length > 0;
            const digits = c.phone_number.replace(/[^0-9]/g, '');
            return !hasName && digits.length <= 1;
        });

        if (valid && allBlank) {
            // Save empty contacts to clear them
            saveTeam({ contacts: [] });
            setSavedContacts([]);
        } else if (valid) {
            // At least one contact has data — require it to be complete
            const hasCompleteContact = contactsRef.current.some((c) => {
                const hasName = c.full_name.trim().length > 0;
                const digits = c.phone_number.replace(/[^0-9]/g, '');
                return hasName && digits.length === 11;
            });
            if (hasCompleteContact) {
                saveTeam({ contacts: contactsRef.current });
                setSavedContacts([...contactsRef.current]);
            }
        }
    }, [saveTeam]);

    const isCompleteContact = (c: { full_name: string; phone_number: string }) => {
        const hasName = c.full_name.trim().length > 0;
        const digits = c.phone_number.replace(/[^0-9]/g, '');
        return hasName && digits.length === 11;
    };

    const hasDirtyContacts = escalation === 'org' && checkOnMeEnabled && (() => {
        const serialize = (arr: { full_name: string; phone_number: string }[]) =>
            arr.map(c => `${c.full_name.trim()}|${c.phone_number}`).join(',');
        return serialize(contacts) !== serialize(savedContacts);
    })();

    // Missing contacts: escalation is "org" but no valid contacts saved.
    // Skip the block only if the team loaded with a pre-existing valid org config (contacts already set).
    const hasMissingContacts = !loadedWithValidOrg && escalation === 'org' && checkOnMeEnabled &&
        !savedContacts.some(isCompleteContact);

    const shouldBlockNavigation = !!(hasDirtyContacts || hasMissingContacts);

    // Block all in-app navigation (sidebar, breadcrumbs, back button, etc.)
    const navBlock = useNavigationBlock(shouldBlockNavigation);

    // Guard browser-level navigation (refresh, close tab)
    useEffect(() => {
        if (!shouldBlockNavigation) return;
        const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [shouldBlockNavigation]);

    const handleDiscardAndNavigate = useCallback(() => {
        if (!teamId) return;
        // Reset escalation to org default since they're discarding
        setContacts([]);
        setSavedContacts([]);
        setContactPhoneErrors([]);
        setContactNameErrors([]);
        setEscalation('ems');
        setIsOverride(false);
        setLoadedWithValidOrg(false);
        // resetTeamSettings now atomically clears contacts, defaultContact, checkOnMeEnabled, and escalationBehavior
        GroupsTeamsService.resetTeamSettings(teamId, (isValid, message) => {
            if (isValid) {
                if (groupId) GroupsTeamsService.fetchTeamDetail(groupId, teamId);
            } else {
                toast.error(message || 'Failed to reset settings.');
            }
        });
        navBlock.proceed();
    }, [navBlock, teamId, groupId, saveTeam]);

    const groupName = l3?.parentGroup?.name || '...';
    const teamName = l3?.team?.name || '...';

    if (loading.teamDetail && !l3) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    return (
        <Box sx={{ px: { xs: 2, md: 3 } }}>
            {/* Breadcrumb */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '4px', fontSize: '13px' }}>
                <Typography
                    component="a"
                    onClick={() => navigate('/groups-teams')}
                    sx={{
                        color: GT_COLORS.primary,
                        textDecoration: 'none',
                        fontWeight: 500,
                        cursor: 'pointer',
                        fontSize: '13px',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    Groups &amp; Teams
                </Typography>
                <Typography sx={{ color: GT_COLORS.textDisabled, fontSize: '11px' }}>›</Typography>
                <Typography
                    component="a"
                    onClick={() => navigate(`/groups-teams/${groupId}`)}
                    sx={{
                        color: GT_COLORS.primary,
                        textDecoration: 'none',
                        fontWeight: 500,
                        cursor: 'pointer',
                        fontSize: '13px',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    {groupName}
                </Typography>
                <Typography sx={{ color: GT_COLORS.textDisabled, fontSize: '11px' }}>›</Typography>
                <Typography sx={{ color: GT_COLORS.textMuted, fontSize: '13px' }}>{teamName}</Typography>
            </Box>

            {/* Page Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '14px' }}>
                <Box>
                    <Typography sx={{ fontSize: '24px', fontWeight: 600, color: GT_COLORS.textPrimary, lineHeight: 1.2 }}>
                        {teamName}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted, mt: '3px' }}>
                        Edit team details, manage members, and configure feature settings.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: '10px' }}>
                    <WatchButton
                        watching={isWatchingTeam(mySubscriptions, teamId, parentGroupId ?? groupId)}
                        onClick={() => {
                            if (!teamId) return;
                            const watching = isWatchingTeam(mySubscriptions, teamId, parentGroupId ?? groupId);
                            GroupsTeamsService.toggleSubscription('team', teamId, watching, (ok) => {
                                if (ok) toast.success(watching ? 'Unsubscribed from alerts.' : 'Subscribed to alerts!');
                                else toast.error('Failed to update subscription.');
                            }, { groupId: parentGroupId ?? groupId });
                        }}
                        size="medium"
                    />
                    {canManageTeams && (
                        <Button
                            variant="outlined"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => setDeleteOpen(true)}
                            sx={{
                                color: GT_COLORS.red,
                                borderColor: GT_COLORS.redBorder,
                                textTransform: 'none',
                                fontSize: '14px',
                                borderRadius: '8px',
                                '&:hover': { backgroundColor: GT_COLORS.redBg, borderColor: GT_COLORS.red },
                            }}
                        >
                            Delete Team
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Card Grid */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px',
                }}
            >
                {/* Collapsible Team Details + Leaders */}
                <Box sx={{ gridColumn: '1 / -1', backgroundColor: GT_COLORS.pageBg, borderRadius: '10px', padding: '12px' }}>
                    {/* Collapsed summary strip */}
                    <Collapse in={!detailsExpanded}>
                        <Box
                            sx={{
                                backgroundColor: GT_COLORS.cardBg,
                                border: `1px solid ${GT_COLORS.border}`,
                                borderLeft: `3px solid ${GT_COLORS.primary}`,
                                borderRadius: '8px',
                                padding: '12px 20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '16px',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap', minWidth: 0 }}>
                                {/* Location */}
                                {location && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '.3px' }}>Location</Typography>
                                        <Typography sx={{ fontSize: '13px', color: GT_COLORS.textPrimary }}>{location}</Typography>
                                    </Box>
                                )}
                                {/* Group */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '.3px' }}>Group</Typography>
                                    <Typography sx={{ fontSize: '13px', color: GT_COLORS.textPrimary }}>{groupName}</Typography>
                                </Box>
                                {/* Leaders */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '.3px' }}>Leaders</Typography>
                                    <Typography sx={{ fontSize: '13px', color: (l3?.leaders || []).length > 0 ? GT_COLORS.textPrimary : GT_COLORS.amber }}>
                                        {(l3?.leaders || []).length > 0
                                            ? (l3?.leaders || []).map((l: any) => l.fullname || [l.firstName, l.lastName].filter(Boolean).join(' ') || l.email || 'Unknown').join(', ')
                                            : 'None assigned'}
                                    </Typography>
                                </Box>
                                {/* Tags */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '.3px' }}>Tags</Typography>
                                    {selectedTags.length > 0 ? (
                                        <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                            {selectedTags.map((t) => (
                                                <Box key={t.id} sx={{ fontSize: '12px', padding: '1px 8px', borderRadius: '4px', backgroundColor: t.color || GT_COLORS.badgeTagBg, color: '#fff' }}>{t.name}</Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography sx={{ fontSize: '13px', color: GT_COLORS.textFaint, fontStyle: 'italic' }}>None</Typography>
                                    )}
                                </Box>
                            </Box>
                            {canManageTeams && (
                                <Button
                                    size="small"
                                    startIcon={<EditIcon sx={{ fontSize: '16px !important' }} />}
                                    onClick={() => setDetailsExpanded(true)}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '13px',
                                        color: GT_COLORS.primary,
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                    }}
                                >
                                    Edit Details
                                </Button>
                            )}
                        </Box>
                    </Collapse>

                    {/* Expanded edit cards */}
                    <Collapse in={detailsExpanded}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <TeamDetailsCard
                                name={name}
                                location={location}
                                groupId={parentGroupId}
                                groupName={groupName}
                                groups={l1Groups.map((g) => ({ id: g.id, name: g.name }))}
                                selectedTags={selectedTags}
                                allTags={allTags}
                                tagsLoading={tagsLoading}
                                onNameChange={setName}
                                onNameBlur={() => {
                                    if (name.trim() && name !== (l3?.team?.name || '')) saveTeam({ name });
                                }}
                                onLocationChange={setLocation}
                                onLocationBlur={() => {
                                    if (location !== ((l3?.team as any)?.location || '')) saveTeam({ location });
                                }}
                                onGroupChange={(v) => {
                                    setParentGroupId(v);
                                    saveTeam({ parentGroupId: v });
                                }}
                                onTagsChange={(t) => {
                                    setSelectedTags(t);
                                    saveTeam({ selectedTags: t });
                                }}
                            />
                            <TeamLeadersCard
                                leaders={l3?.leaders || []}
                                onAddLeader={canManageTeams ? () => setAddLeaderOpen(true) : undefined}
                                onRemoveLeader={canManageTeams ? handleRemoveLeader : undefined}
                            />
                        </Box>
                        <Box
                            onClick={() => setDetailsExpanded(false)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                mt: '12px',
                                padding: '10px 16px',
                                borderRadius: '8px',
                                border: `1px solid ${GT_COLORS.primary}44`,
                                backgroundColor: GT_COLORS.primaryLight,
                                cursor: 'pointer',
                                transition: 'all .15s',
                                '&:hover': { backgroundColor: GT_COLORS.primary, '& *': { color: '#fff !important' } },
                            }}
                        >
                            <KeyboardArrowUpIcon sx={{ fontSize: 20, color: GT_COLORS.primary, transition: 'color .15s' }} />
                            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: GT_COLORS.primary, transition: 'color .15s' }}>
                                Collapse Details
                            </Typography>
                        </Box>
                    </Collapse>
                </Box>

                {/* Section divider */}
                <Box sx={{ gridColumn: '1 / -1', borderTop: `1px solid ${GT_COLORS.borderLight}`, pt: '6px', display: 'flex', alignItems: 'center', gap: '6px', mb: '-4px' }}>
                    <GroupIcon sx={{ fontSize: 15, color: GT_COLORS.textFaint }} />
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '.6px' }}>Team Management</Typography>
                </Box>

                {/* Full width: Members */}
                <Box sx={{ gridColumn: '1 / -1' }}>
                    <TeamMembersCard
                        members={l3?.members || []}
                        onAddMember={canManageTeams ? () => setAddMemberOpen(true) : undefined}
                        onRemoveMember={canManageTeams ? handleRemoveMember : undefined}
                    />
                </Box>

                {/* Section divider */}
                <Box sx={{ gridColumn: '1 / -1', borderTop: `1px solid ${GT_COLORS.borderLight}`, pt: '6px', display: 'flex', alignItems: 'center', gap: '6px', mb: '-4px' }}>
                    <TuneIcon sx={{ fontSize: 15, color: GT_COLORS.textFaint }} />
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '.6px' }}>Feature Configuration</Typography>
                </Box>

                {/* Full width: Feature Configuration */}
                <FeatureConfigCard
                    checkOnMeEnabled={checkOnMeEnabled}
                    escalation={escalation}
                    isOverride={isOverride}
                    onToggleCheckOnMe={(v) => {
                        setCheckOnMeEnabled(v);
                        setIsOverride(true);
                        saveTeam({ checkOnMeEnabled: v });
                    }}
                    onEscalationChange={(v) => {
                        setEscalation(v);
                        // If changing to "org", clear the loaded flag so nav block kicks in
                        if (v === 'org') setLoadedWithValidOrg(false);
                        saveTeam({ escalation: v });
                    }}
                    onResetToDefault={() => {
                        if (!teamId) return;
                        setCheckOnMeEnabled(undefined);
                        setIsOverride(false);
                        GroupsTeamsService.resetTeamSettings(teamId, (isValid, message) => {
                            if (isValid) {
                                toast.success('Settings reset to org default.');
                                if (groupId && teamId) GroupsTeamsService.fetchTeamDetail(groupId, teamId);
                            } else {
                                toast.error(message || 'Failed to reset settings.');
                            }
                        });
                    }}
                />

                {/* Default Emergency Contacts — only when CoM enabled + Org Contacts escalation */}
                {checkOnMeEnabled && escalation === 'org' && (
                    <DefaultContactsCard
                        contacts={contacts}
                        disabled={contactsUnavailable}
                        onChange={setContacts}
                        onSave={validateAndSaveContacts}
                        onClear={(updated) => {
                            const allBlank = updated.every((c) => {
                                const hasName = c.full_name.trim().length > 0;
                                const digits = c.phone_number.replace(/[^0-9]/g, '');
                                return !hasName && digits.length <= 1;
                            });
                            if (allBlank) {
                                saveTeam({ contacts: [] });
                                setSavedContacts([]);
                            } else {
                                saveTeam({ contacts: updated });
                                setSavedContacts([...updated]);
                            }
                        }}
                        phoneErrors={contactPhoneErrors}
                        nameErrors={contactNameErrors}
                    />
                )}

                {/* Full width: Emergency Schedule — only when CoM enabled + Org Contacts escalation */}
                {checkOnMeEnabled && escalation === 'org' && teamId && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                        <EmergencyScheduleSection
                            teamId={teamId}
                            teamName={teamName}
                            defaultContacts={savedContacts}
                        />
                    </Box>
                )}
            </Box>

            {/* Modals */}
            <DeleteTeamModal
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleDelete}
                teamName={teamName}
                loading={loading.saving}
            />

            <AddMemberModal
                open={addMemberOpen}
                onClose={() => setAddMemberOpen(false)}
                teamId={teamId || ''}
                teamName={teamName}
                onAdded={handleAddMemberDone}
            />

            <AddLeaderModal
                open={addLeaderOpen}
                onClose={() => setAddLeaderOpen(false)}
                teamId={teamId || ''}
                teamName={teamName}
                currentLeaderIds={((l3?.team as any)?.leadersIds || l3?.leaders?.map((l: any) => l.id) || []) as string[]}
                teamSnapshot={{
                    name: l3?.team?.name || name,
                    location: l3?.team?.location || location,
                    groupId: (l3?.team as any)?.groupId || l3?.parentGroup?.id || groupId || '',
                    membersIds: ((l3?.team as any)?.membersIds?.length ? (l3?.team as any).membersIds : null) || l3?.members?.map((m: any) => m.id || m._id) || [],
                    tags: (l3?.team as any)?.tags || [],
                    settings: stripCheckOnMeLegacyFields((l3?.team as any)?.settings),
                }}
                onAdded={handleAddLeaderDone}
            />

            {/* Unsaved contacts warning */}
            <Dialog open={navBlock.blocked} onClose={navBlock.reset}>
                <DialogTitle>Unsaved Changes</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {hasMissingContacts
                            ? 'You have selected "Escalate to Dispatch with Org Contacts" but have not set up any emergency contacts. If you leave now, the escalation setting will be reset to the org default.'
                            : 'You have unsaved emergency contact changes. If you leave now, your changes will be discarded and the escalation setting will be reset to the org default.'
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={navBlock.reset}>
                        Go Back
                    </Button>
                    <Button onClick={handleDiscardAndNavigate} color="error">
                        Discard &amp; Reset
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
